import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Tag, ArrowLeft, ExternalLink, Image as ImageIcon, Share2, BarChart, FileText, ChevronLeft, ChevronRight, Copy, X, MessageCircle } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiUrl, getSupabaseHeaders } from '../utils/api';
import { useContact } from '../App.js';


// Define DashboardMetaTags component before DashboardDetail
const DashboardMetaTags = ({ dashboard }) => {
  if (!dashboard) return null;
  
  // Ensure image URL is absolute and WhatsApp-compatible
  const getAbsoluteImageUrl = (url) => {
    if (!url) {
      // Use the main site favicon as fallback, but convert to absolute URL
      return `${window.location.origin}/favicon.svg`;
    }
    
    // If it's already absolute, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Convert relative URL to absolute
    const baseUrl = window.location.origin;
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${cleanUrl}`;
  };

  // Detect image type from URL
  const getImageType = (url) => {
    if (!url) return 'image/png';
    const extension = url.split('.').pop().toLowerCase();
    switch (extension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'webp':
        return 'image/webp';
      case 'svg':
        return 'image/svg+xml';
      default:
        return 'image/jpeg';
    }
  };

  const socialImageUrl = getAbsoluteImageUrl(dashboard.mainImage);
  const imageType = getImageType(socialImageUrl);
  const pageUrl = window.location.href;
  const title = `${dashboard.title} - Arbaz Ahmad Portfolio`;
  const description = dashboard.about || `${dashboard.title} - Power BI Dashboard by Arbaz Ahmad. Interactive data visualization and business intelligence solution.`;
  
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={socialImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content={imageType} />
      <meta property="og:site_name" content="Arbaz Ahmad Portfolio" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={socialImageUrl} />
      <meta name="twitter:site" content="@arbazahmad" />
      
      {/* Additional meta tags for better social sharing */}
      <meta name="author" content="Arbaz Ahmad" />
      <meta name="robots" content="index, follow" />
      <meta name="theme-color" content="#1e40af" />
      
      {/* WhatsApp and other messaging apps rely on Open Graph tags */}
      <meta property="article:author" content="Arbaz Ahmad" />
    </>
  );
};

// PowerBIEmbedRenderer with improved styling and layout
const PowerBIEmbedRenderer = ({ embedUrl, dashboardTitle }) => {
  const [isIframeLoading, setIsIframeLoading] = useState(true);

  if (!embedUrl) {
    return (
      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-8 text-center min-h-[600px] flex items-center justify-center">
        <p className="text-gray-600 dark:text-gray-300">Power BI dashboard link not available.</p>
      </div>
    );
  }

  let isValidPowerBiLink = false;
  try {
    const url = new URL(embedUrl);
    if (url.protocol === 'https:') {
      isValidPowerBiLink = true;
    }
  } catch (_) {
    // Invalid URL format
  }

  if (!isValidPowerBiLink) {
      return (
          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-8 text-center min-h-[600px] flex items-center justify-center">
            <p className="text-red-500 dark:text-red-400">Invalid Power BI URL provided.</p>
          </div>
        );
  }

  return (
    <div className="powerbi-embed-container bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700 w-full max-w-full">
      <div className="p-3 md:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white flex items-center min-w-0 flex-1">
            <BarChart size={20} className="mr-2 md:mr-3 text-blue-600 dark:text-blue-400 md:w-7 md:h-7 flex-shrink-0" />
            <span className="truncate">{dashboardTitle ? `${dashboardTitle} | Preview` : 'Executive Dashboard | Preview'}</span>
          </h2>
          <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
            <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
              <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-1 md:mr-2 animate-pulse"></div>
              <span className="hidden sm:inline">Live Data</span>
              <span className="sm:hidden">Live</span>
            </span>
          </div>
        </div>
        <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1 md:mt-2">Interactive dashboard with real-time data visualization</p>
      </div>
      <div className="relative w-full max-w-full overflow-hidden h-[250px] md:h-[750px] lg:h-[850px]"> 
        {isIframeLoading && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 flex flex-col items-center justify-center z-10">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 dark:border-gray-600 dark:border-t-blue-400"></div>
            <p className="mt-4 text-lg font-medium text-gray-700 dark:text-gray-300">Loading Interactive Dashboard...</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Please wait while we prepare your data visualization</p>
          </div>
        )}
        <iframe
          title={`${dashboardTitle || 'Dashboard'} Preview`}
          src={embedUrl} 
          frameBorder="0"
          allowFullScreen
          className="w-full h-full absolute top-0 left-0 rounded-b-xl max-w-full"
          style={{ minHeight: '400px' }}
          onLoad={() => setIsIframeLoading(false)}
          onError={() => {
            setIsIframeLoading(false);
            console.error("Failed to load Power BI content from URL:", embedUrl);
          }}
        ></iframe>
      </div>
    </div>
  );
};

// Function to update meta tags dynamically
const updateMetaTags = (dashboard) => {
  if (!dashboard) return;

  // Update title
  document.title = `${dashboard.title} - Arbaz Ahmad Portfolio`;

  // Find or create meta tags
  const updateMetaTag = (name, content) => {
    let meta = document.querySelector(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = name;
      document.head.appendChild(meta);
    }
    meta.content = content;
  };

  const updateOgMetaTag = (property, content) => {
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.content = content;
  };

  // Update standard meta tags
  updateMetaTag('description', dashboard.about || `${dashboard.title} - Power BI Dashboard by Arbaz Ahmad`);

  // Update Open Graph meta tags
  updateOgMetaTag('og:title', `${dashboard.title} - Arbaz Ahmad Portfolio`);
  updateOgMetaTag('og:description', dashboard.about || `${dashboard.title} - Power BI Dashboard by Arbaz Ahmad`);
  updateOgMetaTag('og:url', window.location.href);
  
  // Set the dashboard's main image as the og:image if available
  if (dashboard.mainImage) {
    updateOgMetaTag('og:image', dashboard.mainImage);
  }

  // Update Twitter meta tags
  updateOgMetaTag('twitter:title', `${dashboard.title} - Arbaz Ahmad Portfolio`);
  updateOgMetaTag('twitter:description', dashboard.about || `${dashboard.title} - Power BI Dashboard by Arbaz Ahmad`);
  
  // Set the dashboard's main image as the twitter:image if available
  if (dashboard.mainImage) {
    updateOgMetaTag('twitter:image', dashboard.mainImage);
  }
};

// Function to parse markdown-style formatting
const parseMarkdownText = (text) => {
  if (!text) return '';
  
  // Replace **bold** with <strong> tags
  let parsedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Replace *italic* with <em> tags
  parsedText = parsedText.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
  
  // Replace line breaks with <br> tags
  parsedText = parsedText.replace(/\n/g, '<br>');
  
  return parsedText;
};

const DashboardDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { contactInfo } = useContact();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Helper function to format dates safely
  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateString);
        return 'Date not available';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Date not available';
    }
  };

  useEffect(() => {
    if (slug) {
      fetchDashboard();
    } else {
      console.warn('⚠️ No slug provided to DashboardDetail component');
      setError('No dashboard slug provided');
      setLoading(false);
    }
  }, [slug]);

  // Effect to handle dashboard state changes
  useEffect(() => {
    // We no longer need to call updateMetaTags here since we're using DashboardMetaTags component
  }, [dashboard]);

  const fetchDashboard = async () => {
    if (!slug) {
      console.error('Dashboard slug is not available.');
      setError('Dashboard slug is required.');
      setLoading(false);
      return;
    }

    const requestUrl = getApiUrl(`dashboards/${slug}`);

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: {
          ...getSupabaseHeaders(),
          'Accept': 'application/json'
        },
      });



      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Failed to fetch dashboard:', errorText);
        throw new Error(`Dashboard not found. Status: ${response.status} - ${errorText}`);
      }

      const responseData = await response.json();

      // Handle the response structure from Supabase Edge Function
      const data = responseData.data || responseData;
      
      if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
        throw new Error('No dashboard data received from server');
      }
      
      // Normalize field names to handle both camelCase and lowercase variations
      const normalizedDashboard = {
        ...data,
        // Handle tools used field variations
        toolsUsed: data.toolsUsed || data.toolsused || data['toolsUsed'] || [],
        // Handle PowerBI link field variations
        powerBIEmbedLink: data.powerBIEmbedLink || data.powerbiembedlink || data['powerBIEmbedLink'],
        // Handle main image field variations
        mainImage: data.mainImage || data.mainimage || data['mainImage'],
        // Handle additional images field variations
        additionalImages: data.additionalImages || data.additionalimages || data['additionalImages'] || [],
        // Handle created date field variations
        createdDate: data.createdDate || data.createddate || data['createdDate'] || data.createdAt || data.createdat || data['createdAt']
      };
      

      
      setDashboard(normalizedDashboard);
    } catch (err) {
      console.error('💥 Error in fetchDashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); 
    });
  };

  const handleBuyTemplate = () => {
    const dashboardUrl = window.location.href;
    const message = `Hi Arbaz!\n\nI just discovered the "${dashboard.title}" dashboard on your website and I'm really impressed with the design and insights it provides.\n\nI'm interested in purchasing the template or PBIX file with the data model.\n\nCould you please let me know the pricing and availability?\n\nHere's the link to the dashboard: ${dashboardUrl}`;
    const phoneNumber = contactInfo?.whatsapp || '971552342984'; // Fallback to default if not set
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const nextImage = () => {
    if (dashboard) {
      const allImages = [dashboard.mainImage, ...(dashboard.additionalImages || [])];
      if (allImages.length > 1) {
        setCurrentCarouselIndex((prevIndex) => (prevIndex + 1) % allImages.length);
      }
    }
  };

  const prevImage = () => {
    if (dashboard) {
      const allImages = [dashboard.mainImage, ...(dashboard.additionalImages || [])];
      if (allImages.length > 1) {
        setCurrentCarouselIndex((prevIndex) => (prevIndex - 1 + allImages.length) % allImages.length);
      }
    }
  };

  const handleManualNavigation = (index) => {
    setCurrentCarouselIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 5 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const handleArrowClick = (direction) => {
    if (direction === 'next') {
      nextImage();
    } else {
      prevImage();
    }
    setIsAutoPlaying(false);
    // Resume auto-play after 5 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  // Auto-play effect
  useEffect(() => {
    if (!dashboard || !isAutoPlaying) return;
    
    const allImages = [dashboard.mainImage, ...(dashboard.additionalImages || [])];
    if (allImages.length <= 1) return;

    autoPlayRef.current = setInterval(() => {
      setCurrentCarouselIndex((prevIndex) => (prevIndex + 1) % allImages.length);
    }, 3000); // 3 seconds

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [dashboard, isAutoPlaying, currentCarouselIndex]);

  // Pause auto-play on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);
  
  // Get tools used from dashboard data
  const toolsUsed = dashboard?.toolsUsed || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-700 dark:text-gray-300">Loading Dashboard Details...</p>
        </div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-6">
        <div className="text-center bg-white dark:bg-gray-800 p-10 rounded-xl shadow-2xl">
          <h1 className="text-3xl font-bold text-red-600 dark:text-red-400 mb-6">Dashboard Not Found</h1>
          <p className="text-gray-700 dark:text-gray-300 mb-8 text-lg">{error || 'The dashboard you are looking for does not exist or could not be loaded.'}</p>
          <button
            onClick={() => navigate('/gallery')}
            className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          >
            <ArrowLeft size={20} className="inline mr-2" /> Back to Gallery
          </button>
        </div>
      </div>
    );
  }

  const allImages = [dashboard.mainImage, ...(dashboard.additionalImages || [])];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 pt-24 pb-8 px-1 md:px-2 overflow-x-hidden">
      {/* Add DashboardMetaTags component here */}
      {dashboard && <DashboardMetaTags dashboard={dashboard} />}
      
      <style>
        {`
          @keyframes buzz {
            0%, 50%, 100% { transform: scale(1); }
            25% { transform: scale(1.05); }
            75% { transform: scale(1.02); }
          }
        `}
      </style>
      <div className="max-w-none mx-auto w-full px-4 md:px-64">
        {/* Back Button - More prominent and accessible */} 
        <button
          onClick={() => navigate(-1)} 
          className="mb-6 flex items-center text-blue-600 dark:text-blue-400 hover:underline font-medium"
        >
          <ArrowLeft size={20} className="mr-2" /> Back to Gallery
        </button>

        {/* SECTION 1: Dashboard Header */}
        <motion.header 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 shadow-xl rounded-xl p-6 md:p-10 mb-12 border border-gray-200 dark:border-gray-600"
        >
          <div className="grid md:grid-cols-10 gap-8 items-center">
            {/* Left Side (40%) */}
            <div className="md:col-span-4">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 break-words">{dashboard.title}</h1>
              <div className="text-gray-600 dark:text-gray-400 mb-4">
                <p className="text-lg">Created by: <span className="font-semibold text-gray-800 dark:text-gray-200">Arbaz Ahmad</span></p>
                <p className="text-md">Power BI Expert</p>
              </div>
              {toolsUsed.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tools used in this project:</h3>
                  <div className="flex flex-wrap gap-2">
                    {toolsUsed.map((tool, index) => (
                      <span key={`${tool}-${index}`} className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full flex items-center">
                        <Tag size={14} className="mr-1" />
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <motion.button 
                  onClick={handleShare}
                  whileHover={{ 
                    scale: 1.05,
                    y: -2,
                    transition: { type: "spring", stiffness: 400, damping: 25 }
                  }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center justify-center px-6 py-3 rounded-xl font-semibold transition-all duration-300 ease-in-out shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 border
                    ${copied ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white focus:ring-green-400 border-green-400' : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white focus:ring-blue-500 border-blue-400'}`}
                >
                  {copied ? <React.Fragment key="copied"><Copy size={20} className="mr-2" /> Copied!</React.Fragment> : <React.Fragment key="share"><Share2 size={20} className="mr-2" /> Share</React.Fragment>}
                </motion.button>
                
                {/* Buy Template Button - Hero Section */}
                <motion.button
                  onClick={handleBuyTemplate}
                  whileHover={{ 
                    scale: 1.05,
                    y: -2,
                    transition: { type: "spring", stiffness: 400, damping: 25 }
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-xl transition-all duration-300 ease-in-out shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 border border-green-400 animate-pulse hover:animate-none"
                  style={{
                    animation: 'buzz 3s infinite'
                  }}
                >
                  <MessageCircle size={20} className="mr-2" />
                  Buy Template
                </motion.button>
              </div>
            </div>

            {/* Right Side (60%) - Image Gallery */}
            <div className="md:col-span-6">
              {(() => {
                // Create combined array with main image first, then additional images
                const allImages = [dashboard.mainImage, ...(dashboard.additionalImages || [])];
                return (
                  <div className="relative">
                    <div className="text-center mb-4">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center justify-center">
                        <ImageIcon size={24} className="mr-2 text-blue-600 dark:text-blue-400" />
                        Dashboard Views
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Explore different perspectives of this dashboard</p>
                    </div>
                    
                    <div className="relative flex items-center justify-center max-w-4xl mx-auto">
                      <motion.button 
                        onClick={() => handleArrowClick('prev')} 
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 z-10 p-1.5 sm:p-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-full shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed border border-blue-400"
                        disabled={allImages.length <= 1}
                      >
                        <ChevronLeft size={16} className="sm:w-[18px] sm:h-[18px]" />
                      </motion.button>
                      
                      <div 
                        className="w-full overflow-hidden bg-gray-50 dark:bg-gray-900 rounded-xl p-1 sm:p-2 mx-2 sm:mx-6 md:mx-8" 
                        ref={carouselRef}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      >
                        <motion.div 
                          className="flex"
                          key={currentCarouselIndex}
                          initial={{ x: '100%', opacity: 0 }}
                          animate={{ x: '0%', opacity: 1 }}
                          exit={{ x: '-100%', opacity: 0 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        >
                          {allImages.map((image, index) => (
                            <div 
                              key={index} 
                              className="min-w-full flex-shrink-0 flex justify-center items-center"
                              style={{ display: index === currentCarouselIndex ? 'flex' : 'none' }}
                            >
                              <div className="relative group w-full max-w-xs sm:max-w-lg md:max-w-2xl flex items-center justify-center">
                                <img 
                                  src={image} 
                                  alt={index === 0 ? `${dashboard.title} - Main Visual` : `Dashboard view ${index}`} 
                                  className="w-full h-auto object-contain rounded-xl shadow-2xl cursor-pointer transition-transform duration-300 group-hover:scale-105 max-h-[60vh] sm:max-h-[70vh]"
                                  onClick={() => setSelectedImage(image)}
                                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop'; }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-xl transition-all duration-300 flex items-center justify-center pointer-events-none">
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Click to view full size</p>
                                  </div>
                                </div>
                                {/* Main Image Badge */}
                                {index === 0 && (
                                  <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-blue-600 text-white px-2 sm:px-3 py-1 rounded-full text-xs font-medium">
                                    Main View
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </motion.div>
                      </div>

                      <motion.button 
                        onClick={() => handleArrowClick('next')} 
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-10 p-1.5 sm:p-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-full shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed border border-blue-400"
                        disabled={allImages.length <= 1}
                      >
                        <ChevronRight size={16} className="sm:w-[18px] sm:h-[18px]" />
                      </motion.button>
                    </div>
                    
                    {/* Navigation Dots */}
                    {allImages.length > 1 && (
                      <div className="flex flex-col items-center mt-6 space-y-3">
                        <div className="flex justify-center space-x-2">
                          {allImages.map((_, index) => (
                            <motion.button
                              key={index}
                              onClick={() => handleManualNavigation(index)}
                              whileHover={{ scale: 1.25 }}
                              whileTap={{ scale: 0.9 }}
                              className={`w-3 h-3 rounded-full transition-all duration-300 transform focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                currentCarouselIndex === index 
                                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg scale-110 border border-blue-400' 
                                  : 'bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-500 hover:from-blue-400 hover:to-blue-500 dark:hover:from-blue-500 dark:hover:to-blue-400 border border-gray-400 dark:border-gray-500'
                              }`}
                              aria-label={`Go to image ${index + 1}`}
                            />
                          ))}
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Image {currentCarouselIndex + 1} of {allImages.length}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </motion.header>

        {/* SECTION 2: Main Content - Vertical Layout */}
        <div className="flex flex-col gap-8 mb-10">
          {/* Power BI Preview Section - Now on Top */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full"
          >
            <div className="w-full overflow-hidden">
              <PowerBIEmbedRenderer embedUrl={dashboard.powerBIEmbedLink} dashboardTitle={dashboard.title} />
            </div>
          </motion.section>

          {/* Dashboard Info Section - Now Below */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="w-full"
          >
            <div className="bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 shadow-xl rounded-xl p-6 md:p-12 min-h-[600px] border border-gray-200 dark:border-gray-600">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-8 flex items-center justify-center md:justify-start">
                <BarChart size={28} className="mr-3 text-blue-600 dark:text-blue-400" />
                Dashboard Info
              </h2>
              
              {/* Content Grid for Desktop, Stack for Mobile - Two Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 md:gap-8 lg:divide-x lg:divide-gray-200 dark:lg:divide-gray-600">
                 {/* Left Column - Category, Created Date, and Action Buttons */}
                  <div className="lg:col-span-2 space-y-6 lg:pr-6">
                   {/* Category */}
                   <div>
                     <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Category</h3>
                     <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                       {dashboard.category || 'Business Intelligence'}
                     </span>
                   </div>

                   {/* Created Date */}
                   <div>
                     <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Created</h3>
                     <div className="flex items-center text-gray-600 dark:text-gray-400">
                       <Calendar size={18} className="mr-2" />
                       <span className="text-base">{formatDate(dashboard.createdDate || dashboard.createdAt || dashboard.created_date)}</span>
                     </div>
                   </div>

                   {/* Tags */}
                   {dashboard.tags && dashboard.tags.length > 0 && (
                     <div>
                       <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Tags</h3>
                       <div className="flex flex-wrap gap-2">
                         {dashboard.tags.map((tag, index) => (
                           <span key={`${tag}-${index}`} className="text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full">
                             #{tag}
                           </span>
                         ))}
                       </div>
                     </div>
                   )}

                   {/* Action Buttons */}
                   <div className="space-y-4 mt-8">
                     <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Actions</h3>
                     
                     {/* View Report Button */}
                     {dashboard.powerBIEmbedLink && (
                       <motion.a
                         href={dashboard.powerBIEmbedLink}
                         target="_blank"
                         rel="noopener noreferrer"
                         whileHover={{ 
                           scale: 1.02,
                           y: -2,
                           transition: { type: "spring", stiffness: 400, damping: 25 }
                         }}
                         whileTap={{ scale: 0.98 }}
                         className="w-full flex items-center justify-center px-4 py-2.5 h-12 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium rounded-lg transition-all duration-300 ease-in-out shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 border border-blue-400 whitespace-nowrap overflow-hidden text-ellipsis"
                       >
                         <ExternalLink size={20} className="mr-2" />
                         View Full Report
                       </motion.a>
                     )}
                     
                     {/* Buy Template Button */}
                     <motion.button
                       onClick={handleBuyTemplate}
                       whileHover={{ 
                         scale: 1.02,
                         y: -2,
                         transition: { type: "spring", stiffness: 400, damping: 25 }
                       }}
                       whileTap={{ scale: 0.98 }}
                       className="w-full flex items-center justify-center px-4 py-2.5 h-12 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium rounded-lg transition-all duration-300 ease-in-out shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 border border-green-400 hover:animate-none whitespace-nowrap overflow-hidden text-ellipsis"
                       style={{
                         animation: 'buzz 3s infinite'
                       }}
                     >
                       <MessageCircle size={20} className="mr-2" />
                       Buy Template
                     </motion.button>
                     <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                       Get the PBIX file and data model
                     </p>
                   </div>
                 </div>
 
                 {/* Right Column - About Section */}
                  <div className="lg:col-span-4 lg:pl-6">
                   <div>
                     <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">About This Dashboard</h3>
                     <div className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                       {dashboard.about && dashboard.about.split('\n').map((paragraph, index) => (
                         <p 
                           key={index} 
                           className="mb-3"
                           dangerouslySetInnerHTML={{ __html: parseMarkdownText(paragraph) }}
                         />
                       ))}
                     </div>
                   </div>
                 </div>
               </div>
            </div>
          </motion.section>
        </div>


      </div>

      {/* Enhanced Image Modal with Navigation */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[100] p-4 md:p-8"
          onClick={() => setSelectedImage(null)}
        >
          {/* Navigation Arrows */}
          {dashboard.additionalImages && dashboard.additionalImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const currentIndex = dashboard.additionalImages.indexOf(selectedImage);
                  const prevIndex = currentIndex === 0 ? dashboard.additionalImages.length - 1 : currentIndex - 1;
                  setSelectedImage(dashboard.additionalImages[prevIndex]);
                }}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-[110] p-3 bg-white/20 hover:bg-white/30 text-white rounded-full shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 hover:scale-110"
                aria-label="Previous image"
              >
                <ChevronLeft size={32} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const currentIndex = dashboard.additionalImages.indexOf(selectedImage);
                  const nextIndex = currentIndex === dashboard.additionalImages.length - 1 ? 0 : currentIndex + 1;
                  setSelectedImage(dashboard.additionalImages[nextIndex]);
                }}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-[110] p-3 bg-white/20 hover:bg-white/30 text-white rounded-full shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 hover:scale-110"
                aria-label="Next image"
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}
          
          {/* Main Image */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt="Full size view"
              className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
            />
            
            {/* Image Counter */}
            {dashboard.additionalImages && dashboard.additionalImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
                {dashboard.additionalImages.indexOf(selectedImage) + 1} of {dashboard.additionalImages.length}
              </div>
            )}
          </motion.div>
          
          {/* Close Button */}
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 md:top-6 md:right-6 text-white bg-black/50 p-3 rounded-full hover:bg-black/75 transition-colors z-[110]"
            aria-label="Close image viewer"
          >
            <X size={28} />
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default DashboardDetail;